// src/services/api.js
import axios from 'axios';

const AUTH_URL = 'https://jessster-backend.onrender.com/auth'; // Authentication base URL
const API_URL = 'https://jessster-backend.onrender.com/api'; // API base URL for actions


// Set up Axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',  // Optional for GET, but good practice
    },
});

// Add a request interceptor
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken'); // Retrieve the token from local storage
    if (token) {
        config.headers.Authorization = `Token ${token}`; // Assuming your backend uses 'Token' scheme
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Function to register a new user
export const registerUser = async (userData) => {
    try {
        const response = await axios.post(`${AUTH_URL}/registration/`, userData);
        return response.data; // Returns the user data from the response
    } catch (error) {
        throw error.response.data; // Customize this based on your needs
    }
};

// Function to log in a user
export const loginUser = async (credentials) => {
    try {
        const response = await axios.post(`${AUTH_URL}/login/`, credentials);
        console.log('Login Response:', response.data); // Log the response data
        // Store user ID in local storage
        localStorage.setItem('user', response.data.user); // Use 'userId' for consistency
        console.log('User ID saved:', response.data.user);
        return { token: response.data.key }; // Return the token
    } catch (error) {
        throw error.response.data;
    }
};

// Function to fetch user profile in side navigation menu
export const fetchUserProfile = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Returns the user profile data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to fetch the user's profile
export const fetchProfile = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Returns the profile data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to update the user's profile
export const updateProfile = async (profileData, token) => {
    try {
        const response = await axios.patch(`${API_URL}/profile/`, profileData, {
            headers: { Authorization: `Token ${token}` },
        });
        return response.data; // Returns the updated profile data
    } catch (error) {
        throw error.response?.data || error.message; // Handle the error response
    }
};


// Function to delete the user's profile
export const deleteProfile = async (token) => {
    try {
        const response = await axios.delete(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Optionally return a success message or confirmation
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to fetch all blog posts
export const fetchPosts = async () => {
    try {
        const response = await api.get('/posts/');  // Adjusted to use axios
        return response.data;  // Assuming the data is an array of posts
    } catch (error) {
        console.error('Error fetching posts:', error);
        return [];  // Return an empty array in case of an error
    }
};

// Function to fetch a single post by slug
export const fetchPostBySlug = async (slug) => {
    try {
        const response = await api.get(`/posts/${slug}/`);  // Adjust the endpoint as per your Django API
        return response.data;  // Return the post data
    } catch (error) {
        console.error('Error fetching post by slug:', error);
        throw error.response ? error.response.data : error;
    }
};

// Function to fetch all categories by language
export const fetchCategories = async (language = 'en') => {
    try {
        const response = await api.get(`/categories/`, {
            params: { language }, // Add the language as a query parameter
        });
        return response.data;  // Return the list of categories
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error.response ? error.response.data : error;
    }
};


// Function to fetch all videos by language
export const fetchVideos = async (language = 'en') => {
    try {
        const response = await api.get('/videos/', {
            params: { lang: language },  // Pass the 'lang' as a query parameter instead of 'language'
        });
        return response.data;  // Returning the videos array
    } catch (error) {
        // Log the error response to help with debugging
        if (error.response) {
            console.error('Error fetching videos:', error.response.status);
            console.error('Error response data:', error.response.data);
        } else {
            console.error('Error:', error.message);
        }
        return [];  // Return an empty array in case of an error
    }
};


// Function to fetch a single video by id/slug and language
export const fetchVideoBySlug = async (id, language = 'en') => {
    try {
        const response = await api.get(`/videos/${id}/`, {
            params: { lang: language },  // Pass the 'lang' as a query parameter instead of 'language'
        });
        return response.data;  // Return the video data
    } catch (error) {
        console.error('Error fetching video by slug:', error);
        throw error.response ? error.response.data : error;  // Propagate error if needed
    }
};


// Function to toggle like status for a post
export const toggleLike = async (slug, token) => {
    try {
        const response = await api.post(`/posts/${slug}/like/`, {}, {
            headers: {
                Authorization: `Token ${token}`,  // Token for authentication
            },
        });
        return response.data; // Ensure the API returns necessary data
    } catch (error) {
        console.error('Error toggling like:', error);
        throw error.response ? error.response.data : error;
    }
};

// --------------- Comments API ------------------

// Function to fetch comments for a specific post
export const fetchComments = async (postSlug) => {
    try {
        const response = await api.get(`/posts/${postSlug}/comments/`);
        return response.data; // Returns an array of comments for the post
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};

// Function to add a new comment to a specific post
export const addComment = async (postSlug, commentData, token) => {
    try {
        const response = await api.post(`/posts/${postSlug}/comments/`, commentData, {
            headers: {
                Authorization: `Token ${token}`,  // Token for authentication
            },
        });
        return response.data; // Returns the created comment
    } catch (error) {
        console.error('Error adding comment:', error);
        throw error.response ? error.response.data : error;
    }
};


// Function to fetch blog posts sorted by most liked
export const fetchMostLikedPosts = async () => {
    try {
        const response = await api.get('/posts/', {
            params: {
                sort_by: 'likes',  // Sort by likes
                order: 'desc'      // Descending order (most liked first)
            }
        });
        return response.data;  // Assuming the data is an array of posts
    } catch (error) {
        console.error('Error fetching most liked posts:', error);
        return [];  // Return an empty array in case of an error
    }
};


// Function to fetch blog posts sorted by most comments
export const fetchMostCommentedPosts = async () => {
    try {
        const response = await api.get('/posts/', {
            params: {
                sort_by: 'comments',  // Sort by comments
                order: 'desc'         // Descending order (most commented first)
            }
        });
        return response.data;  // Assuming the data is an array of posts
    } catch (error) {
        console.error('Error fetching most commented posts:', error);
        return [];  // Return an empty array in case of an error
    }
};


// Function to search for posts based on a query
export const fetchSearchResults = async (query) => {
    try {
        const response = await api.get('/search/', {
            params: { q: query }  // Passing the query as a parameter with the correct key
        });
        return response.data;  // Returning the results from the API
    } catch (error) {
        console.error('Error fetching search results:', error);
        return { results: [] };  // Returning an empty result array in case of error
    }
};


// Function to fetch liked articles
export const fetchLikedArticles = async () => {
    try {
        const response = await api.get('/user/liked-articles/');
        return response.data; // Returns the liked articles data
    } catch (error) {
        console.error('Error fetching liked articles:', error);
        throw error.response?.data || error; // Handle the error response
    }
};


// Function to subscribe a user (send email to backend)
export const subscribeEmail = async (email) => {
    try {
        const response = await axios.post(`${API_URL}/subscribe/`, { email });
        return response.data; // Returns the success message from the response
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to fetch the latest 13 posts on home pages, with optional language filter
export const fetchLatestPosts = async (language) => {
    try {
        const response = await api.get('/articles/limited/', {
            params: {
                language: language, // Pass the language as a query parameter
            },
        });
        return response.data; // Assuming the data is an array of posts
    } catch (error) {
        console.error('Error fetching latest posts:', error);
        return []; // Return an empty array in case of an error
    }
};


// Function to fetch the 4 most viewed posts on home pages
export const fetchMostViewedPosts = async (language) => {
    try {
        const response = await api.get('/articles/most-viewed/', {  // Updated URL
            params: {
                language: language,  // Pass language as a parameter (e.g., 'en', 'ar', 'ru')
                limit: 12             // Limit to 4 posts
            }
        });
        return response.data;  // Assuming the response data is an array of posts
    } catch (error) {
        console.error('Error fetching most viewed posts:', error);
        return [];  // Return an empty array in case of an error
    }
};


// Function to fetch all images by language
export const fetchImages = async (language = 'en') => {
    try {
        const response = await api.get('/images/', {
            params: { lang: language }, // Pass the 'lang' as query parameter
        });
        return response.data; // Returns array of images
    } catch (error) {
        if (error.response) {
            console.error('Error fetching images:', error.response.status);
            console.error('Error response data:', error.response.data);
        } else {
            console.error('Error:', error.message);
        }
        return []; // Return empty array on error
    }
};

// Function to fetch a single image by id and language
export const fetchImageById = async (id, language = 'en') => {
    try {
        const response = await api.get(`/images/${id}/`, {
            params: { lang: language }, // Pass the 'lang' as query parameter
        });
        return response.data; // Return single image data
    } catch (error) {
        console.error('Error fetching image by id:', error);
        throw error.response ? error.response.data : error; // Propagate error if needed
    }
};


// Function to fetch all audio files by language
export const fetchAudios = async (language = 'en') => {
    try {
        const response = await api.get('/audios/', {
            params: { lang: language }, // Pass the 'lang' as query parameter
        });
        return response.data; // Returns array of audio files
    } catch (error) {
        if (error.response) {
            console.error('Error fetching audios:', error.response.status);
            console.error('Error response data:', error.response.data);
        } else {
            console.error('Error:', error.message);
        }
        return []; // Return empty array on error
    }
};

// Function to fetch a single audio file by id and language
export const fetchAudioById = async (id, language = 'en') => {
    try {
        const response = await api.get(`/audios/${id}/`, {
            params: { lang: language }, // Pass the 'lang' as query parameter
        });
        return response.data; // Return single audio file data
    } catch (error) {
        console.error('Error fetching audio by id:', error);
        throw error.response ? error.response.data : error; // Propagate error if needed
    }
};
























// =====================================================
// User Posts (Community / Approved)
// =====================================================

// Fetch all published posts with optional filters and sorting
export const fetchUserPosts = async (params = {}) => {
    try {
        const response = await api.get('/user-posts/posts/', { params });
        return response.data;
    } catch (err) {
        console.error('Error fetching user posts:', err);
        return [];
    }
};

// Fetch a single published post by slug
export const fetchUserPostBySlug = async (slug) => {
    try {
        const response = await api.get(`/user-posts/posts/${slug}/`);
        return response.data;
    } catch (err) {
        console.error('Error fetching user post:', err);
        throw err.response ? err.response.data : err;
    }
};

// =====================================================
// User Dashboard / Author Actions
// =====================================================

// Fetch all posts created by the authenticated user
export const fetchMyPosts = async (token, params = {}) => {
    try {
        const response = await api.get('/user-posts/my-posts/', {
            headers: { Authorization: `Token ${token}` },
            params,
        });
        return response.data;
    } catch (err) {
        console.error('Error fetching my posts:', err);
        return [];
    }
};

// Create a new user post (draft)
export const createUserPost = async (formData, token) => {
    try {
        const response = await api.post('/user-posts/posts/create/', formData, {
            headers: { Authorization: `Token ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (err) {
        console.error('Error creating user post:', err);
        throw err.response ? err.response.data : err;
    }
};


// Update a draft or denied post
export const updateUserPost = async (slug, formData, token) => {
    try {
        const response = await api.put(`/user-posts/posts/${slug}/edit/`, formData, {
            headers: { Authorization: `Token ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (err) {
        console.error('Error updating user post:', err);
        throw err.response ? err.response.data : err;
    }
};


// Submit a draft or denied post for review
export const submitUserPost = async (slug, token) => {
    try {
        const response = await api.post(`/user-posts/posts/${slug}/submit/`, {}, {
            headers: { Authorization: `Token ${token}` },
        });
        return response.data;
    } catch (err) {
        console.error('Error submitting user post:', err);
        throw err.response ? err.response.data : err;
    }
};

// Delete a user post by slug
export const deleteUserPost = async (slug, token) => {
    try {
        const response = await api.delete(`/user-posts/posts/${slug}/delete/`, {
            headers: { Authorization: `Token ${token}` },
        });
        return response.data; // might contain { detail: "Deleted" }
    } catch (err) {
        console.error('Error deleting user post:', err);
        throw err.response ? err.response.data : err;
    }
};


// Function to fetch liked user-submitted articles
export const fetchMyLikedUserArticles = async (token) => {
    try {
        const response = await api.get('/user-posts/my-liked-articles/', {
            headers: {
                Authorization: `Token ${token}`,
            },
        });
        // Add a flag for user-submitted posts
        const articlesWithFlag = response.data.map(article => ({
            ...article,
            is_user_submitted: true,
        }));
        return articlesWithFlag; 
    } catch (error) {
        console.error('Error fetching liked user articles:', error);
        throw error.response?.data || error;
    }
};


// Function to like/unlike a user-submitted post
export const toggleUserPostLike = async (slug) => {
    const token = localStorage.getItem('authToken');
    try {
        const response = await api.post(`/user-posts/posts/${slug}/like/`, null, {
            headers: {
                Authorization: `Token ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error toggling like on user post:', error);
        throw error.response?.data || error;
    }
};



// =====================================================
// Categories
// =====================================================

// Fetch all categories
export const fetchUserCategories = async (token) => {
    try {
        const response = await api.get('/user-posts/categories/', {
            headers: { Authorization: `Token ${token}` },
        });
        return response.data;
    } catch (err) {
        console.error('Error fetching user categories:', err);
        return [];
    }
};

// =====================================================
// Comments
// =====================================================

// Fetch comments for a specific post
export const fetchUserComments = async (postSlug) => {
    try {
        const response = await api.get(`/user-posts/posts/${postSlug}/comments/`);
        return response.data;
    } catch (err) {
        console.error('Error fetching comments:', err);
        throw err.response ? err.response.data : err;
    }
};

// Add a comment to a post (authenticated)
export const addUserComment = async (postSlug, commentData, token) => {
    try {
        const response = await api.post(`/user-posts/posts/${postSlug}/comments/`, commentData, {
            headers: { Authorization: `Token ${token}` },
        });
        return response.data;
    } catch (err) {
        console.error('Error adding comment:', err);
        throw err.response ? err.response.data : err;
    }
};













/* ---------------------------
   Clips API functions
--------------------------- */

// Fetch paginated clips feed (fetch clips in order from newest to oldest)
{/*
export const fetchClipsFeed = async ({ nextUrl = null, language = null } = {}) => {
    try {
        let response;

        if (nextUrl) {
            
            response = await api.get(nextUrl);
        } else {
            
            const params = {};
            if (language) params.language = language;

            response = await api.get('/clips/feed/', { params });
        }

        
        return response.data;
    } catch (error) {
        console.error('Error fetching clips feed:', error);
        throw error.response?.data || error;
    }
};
*/}

// Fetch paginated clips feed - fetch in random order
export const fetchClipsFeed = async ({
  nextUrl = null,
  language = null,
  seed = null,
} = {}) => {
  try {
    let response;

    if (nextUrl) {
      // Cursor URL already contains seed & language
      response = await api.get(nextUrl);
    } else {
      // First request: send language + seed
      const params = {};
      if (language) params.language = language;
      if (seed) params.seed = seed;

      response = await api.get('/clips/feed/', { params });
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching clips feed:', error);
    throw error.response?.data || error;
  }
};

// Fetch a truly random clip (used for fullscreen tap)
export const fetchRandomClip = async () => {
  try {
    const response = await api.get('/clips/random/');
    // Returns: { id, title, description }
    return response.data;
  } catch (error) {
    console.error('Error fetching random clip:', error);
    throw error.response?.data || error;
  }
};



// Fetch a single clip detail by ID
export const fetchClipDetail = async (clipId) => {
    try {
        const response = await api.get(`/clips/${clipId}/`);
        // Returns: { id, title, description (Drive link) }
        return response.data;
    } catch (error) {
        console.error(`Error fetching clip ${clipId}:`, error);
        throw error.response?.data || error;
    }
};

/* ---------------------------
   Specials API functions
--------------------------- */
// Fetch all comedians (grid page)
export const fetchComedians = async (ordering = null) => {
  try {
    const params = {};
    if (ordering) params.ordering = ordering;

    const response = await api.get('/specials/comedians/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching comedians:', error);
    throw error;
  }
};

// Fetch comedian detail + their specials
export const fetchComedianDetail = async (comedianId) => {
  try {
    const response = await api.get(`/specials/comedians/${comedianId}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching comedian ${comedianId}:`, error);
    throw error.response?.data || error;
  }
};


/* ---------------------------
   Comedy Movies API functions
--------------------------- */

// Fetch all published comedy movies
export const fetchComedyMovies = async (ordering = null) => {
  try {
    const params = {};
    if (ordering) params.ordering = ordering;

    const response = await api.get('/comedies/movies/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching comedy movies:', error);
    throw error;
  }
};

// Fetch a single comedy movie
export const fetchComedyMovieDetail = async (movieId) => {
  try {
    const response = await api.get(`/comedies/movies/${movieId}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching comedy movie ${movieId}:`, error);
    throw error.response?.data || error;
  }
};